import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Edit3, Monitor, Plus, Settings, Trash2 } from "lucide-react";

interface McpServer {
  id: string;
  name: string;
  status: string;
  url: string;
}

interface SystemManagementProps {
  systemPrompt: string;
  mcpServers: McpServer[];
  mcpLoading: boolean;
}

export function SystemManagement({
  systemPrompt,
  mcpServers,
  mcpLoading,
}: SystemManagementProps) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* System Prompt Editor */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Edit3 className="w-5 h-5" />
            System Prompt (dummy UI)
          </CardTitle>
          <Button variant="outline" size="sm">
            Reset to Default
          </Button>
        </CardHeader>
        <CardContent>
          <Textarea
            value={systemPrompt}
            className="min-h-[200px] font-mono text-sm"
            placeholder="Enter your system prompt..."
          />
          <div className="flex justify-between items-center mt-4">
            <p className="text-xs text-muted-foreground">
              {systemPrompt.length} characters
            </p>
            <Button size="sm">Update Prompt</Button>
          </div>
        </CardContent>
      </Card>

      {/* MCP Servers Management */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5" />
            MCP Servers
          </CardTitle>
          <Button size="sm">
            <Plus className="w-4 h-4 mr-1" />
            Add Server
          </Button>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {mcpLoading ? (
              <div className="text-sm text-muted-foreground">
                Loading MCP servers...
              </div>
            ) : mcpServers.length === 0 ? (
              <div className="text-sm text-muted-foreground">
                No MCP servers configured
              </div>
            ) : (
              mcpServers.map((server) => (
                <div
                  key={server.id}
                  className="flex items-center justify-between p-3 border rounded-lg"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-sm">{server.name}</span>
                      <Badge
                        variant={
                          server.status === "ready"
                            ? "default"
                            : server.status === "failed"
                              ? "destructive"
                              : "secondary"
                        }
                      >
                        {server.status}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground font-mono">
                      {server.url}
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm">
                      <Edit3 className="w-3 h-3" />
                    </Button>
                    <Button variant="ghost" size="sm">
                      <Trash2 className="w-3 h-3 text-destructive" />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Context Management */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Monitor className="w-5 h-5" />
            Context Control (dummy UI)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label htmlFor="max-context-length" className="text-sm font-medium">
              Max Context Length
            </label>
            <Input
              id="max-context-length"
              type="number"
              defaultValue="4000"
              className="mt-1"
            />
          </div>
          <div>
            <label htmlFor="temperature" className="text-sm font-medium">
              Temperature
            </label>
            <Input
              id="temperature"
              type="number"
              step="0.1"
              defaultValue="0.7"
              className="mt-1"
            />
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="flex-1">
              Clear Context
            </Button>
            <Button variant="outline" size="sm" className="flex-1">
              Save Session
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
